import { useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { DGetSelfMedicationMessages } from '../data/dtos/message';
import { MChatMessage } from '../models/chat-message';
import { selfMedicationChatService } from '../services/self-medication';

const PAGE_SIZE = 20;

export function useSelfMedicationMessages({
  userId,
}: Nullable<Pick<DGetSelfMedicationMessages, 'userId'>>) {
  const execute = async ([_, __, lastMessageId]: [
    string,
    number,
    number | null,
  ]) => {
    return await selfMedicationChatService.getMessages({
      userId: userId!,
      lastMessageId,
    });
  };

  const fetchKeyGenerator = (
    _: number,
    previousPageData: MChatMessage[] | null
  ) => {
    if (!userId) {
      return null;
    }
    return [
      'get-self-medication-messages',
      userId,
      previousPageData?.[0]?.props.id ?? null, // Last message Id
    ];
  };
  const { data, error, isLoading, isValidating, mutate, size, setSize } =
    useSWRInfinite(fetchKeyGenerator, execute);

  // Reverse the order of messages to display the latest message at the bottom
  const messages = useMemo(() => {
    if (!data) {
      return [];
    }
    return data
      .toReversed()
      .flat()
      .filter((message) => message.props.content || message.hasAttachments);
  }, [data]);
  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isRefreshing = (isValidating && data && data.length === size) ?? false;
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.length < PAGE_SIZE);

  const addMessage = (message: MChatMessage) => {
    mutate((pages) => {
      if (!pages) {
        return pages;
      }
      return [[message], ...pages];
    }, false);
  };

  const refresh = () => {
    setSize(0);
    mutate();
  };

  const loadMore = () => setSize((size) => size + 1);

  return {
    messages,
    loadMore,
    refresh,
    error,
    isLoading,
    isLoadingMore,
    isRefreshing,
    isReachingEnd,
    addMessage,
  };
}
